/* Trend Products — quick-add card (DEV-15415, copied from Editorial Products DEV-15282).
   Each card holds one or more products: the block product plus its colour
   siblings (metafield-driven swatches, each one a different product). Picking
   a swatch swaps the card to that product and rebuilds its variant dropdowns.
   Adds through the Ajax cart and dispatches `product:added`, which the
   theme's CartSection listens to in order to re-render and open the drawer.
   Below 641px the panel opens as a modal popup over a scrim (the card stays
   in the grid); from 641px it replaces the card front in place. */

const MODAL_QUERY = window.matchMedia('(max-width: 640px)');

class TrendProductCard extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-card-json]');
    if (!json) return;

    this.data = JSON.parse(json.textContent);
    this.front = this.querySelector('[data-card-front]');
    this.panel = this.querySelector('[data-card-panel]');
    this.scrim = this.querySelector('[data-card-scrim]');
    this.openButton = this.querySelector('.TrendProductCard__Toggle');
    this.optionsContainer = this.querySelector('[data-card-options]');
    this.dropdownTemplate = this.querySelector('[data-dropdown-template]');
    this.swatchDropdown = this.querySelector('[data-swatch-dropdown]');
    this.addButton = this.querySelector('[data-action="add-to-cart"]');
    this.addLabel = this.querySelector('[data-add-to-cart-label]');
    this.waitlist = this.querySelector('[data-card-waitlist]');
    this.error = this.querySelector('[data-card-error]');
    this.labels = {
      select: this.dataset.selectLabel,
      add: this.addLabel.textContent.trim(),
      soldOut: this.dataset.soldOutLabel,
      unavailable: this.dataset.unavailableLabel
    };

    // Every swatch product sold out: no selectors at all, straight to the waitlist.
    this.allSoldOut = this.data.products.every((product) => this.isSoldOut(product));
    this.swatchChosen = !this.data.hasSwatches || this.allSoldOut;
    if (this.swatchDropdown) this.swatchDropdown.hidden = this.allSoldOut;
    this.selected = [];

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);

    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('click', this.onDocumentClick);
    MODAL_QUERY.addEventListener('change', this.onBreakpointChange);

    this.setProduct(this.data.activeIndex);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('keydown', this.onKeydown);
    document.removeEventListener('click', this.onDocumentClick);
    MODAL_QUERY.removeEventListener('change', this.onBreakpointChange);
    if (this.isModal) document.documentElement.classList.remove('no-scroll');
  }

  get dropdowns() {
    return [...this.querySelectorAll('.TrendProductCard__Dropdown')];
  }

  get optionDropdowns() {
    return [...this.optionsContainer.children];
  }

  onClick(event) {
    const target = event.target.closest('[data-action]');
    if (!target || !this.contains(target)) return;

    switch (target.dataset.action) {
      case 'open-panel':
        this.openPanel();
        break;
      case 'close-panel':
        this.closePanel();
        break;
      case 'toggle-dropdown':
        this.toggleDropdown(target.closest('.TrendProductCard__Dropdown'));
        break;
      case 'select-option':
        this.selectOption(target);
        break;
      case 'select-product':
        this.selectProduct(target);
        break;
      case 'add-to-cart':
        this.addToCart();
        break;
      case 'open-zoom':
        this.openZoom(target);
        break;
    }
  }

  onKeydown(event) {
    if (event.key === 'Tab' && this.isModal) {
      this.trapFocus(event);
      return;
    }

    if (event.key !== 'Escape') return;

    const openDropdown = this.dropdowns.find((dropdown) => dropdown.classList.contains('is-open'));

    if (openDropdown) {
      this.toggleDropdown(openDropdown, false);
      openDropdown.querySelector('[data-action="toggle-dropdown"]').focus();
    } else if (!this.panel.hidden) {
      this.closePanel();
    }
  }

  onDocumentClick(event) {
    this.dropdowns.forEach((dropdown) => {
      if (!dropdown.contains(event.target)) this.toggleDropdown(dropdown, false);
    });
  }

  // Popup and in-place layouts differ, so an open panel doesn't survive a breakpoint switch.
  onBreakpointChange() {
    if (this.isOpen) this.closePanel({ restoreFocus: false });
  }

  trapFocus(event) {
    const focusable = [...this.panel.querySelectorAll('button, a[href]')]
      .filter((element) => !element.disabled && element.offsetParent !== null);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (!first) return;

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  isSoldOut(product) {
    return product.variants.every((variant) => !variant.available);
  }

  get isOpen() {
    return !this.panel.hidden;
  }

  openPanel() {
    // Only one panel open on the page: close the others without moving focus.
    document.querySelectorAll('trend-product-card').forEach((card) => {
      if (card !== this && card.isOpen) card.closePanel({ restoreFocus: false });
    });

    this.isModal = MODAL_QUERY.matches;
    this.setModalState(this.isModal);

    this.front.hidden = !this.isModal;
    this.panel.hidden = false;
    this.openButton.setAttribute('aria-expanded', 'true');
    const focusTarget = [...this.panel.querySelectorAll('[data-action="toggle-dropdown"], [data-action="add-to-cart"], [data-card-waitlist]')]
      .find((element) => element.offsetParent !== null);
    focusTarget?.focus();
  }

  closePanel({ restoreFocus = true } = {}) {
    this.dropdowns.forEach((dropdown) => this.toggleDropdown(dropdown, false));
    this.setModalState(false);
    this.isModal = false;

    this.panel.hidden = true;
    this.front.hidden = false;
    this.openButton.setAttribute('aria-expanded', 'false');
    if (restoreFocus) this.openButton.focus();
  }

  setModalState(isModal) {
    this.scrim.hidden = !isModal;
    this.panel.setAttribute('role', isModal ? 'dialog' : 'group');
    if (isModal) {
      this.panel.setAttribute('aria-modal', 'true');
    } else {
      this.panel.removeAttribute('aria-modal');
    }
    document.documentElement.classList.toggle('no-scroll', isModal);
  }

  toggleDropdown(dropdown, force) {
    const isOpen = force ?? !dropdown.classList.contains('is-open');

    if (isOpen) {
      this.dropdowns.forEach((other) => other !== dropdown && this.toggleDropdown(other, false));
    }

    dropdown.classList.toggle('is-open', isOpen);
    dropdown.querySelector('[data-action="toggle-dropdown"]').setAttribute('aria-expanded', String(isOpen));
    dropdown.querySelector('.TrendProductCard__DropdownList').hidden = !isOpen;
  }

  commitChoice(dropdown, chosenButton) {
    dropdown.querySelectorAll('.TrendProductCard__DropdownOption').forEach((button) => {
      button.setAttribute('aria-pressed', String(button === chosenButton));
    });
    dropdown.querySelector('[data-dropdown-label]').textContent = chosenButton.dataset.value;
    this.toggleDropdown(dropdown, false);
    dropdown.querySelector('[data-action="toggle-dropdown"]').focus();
  }

  selectOption(optionButton) {
    const dropdown = optionButton.closest('.TrendProductCard__Dropdown');

    this.selected[Number(dropdown.dataset.optionIndex)] = optionButton.dataset.value;
    this.commitChoice(dropdown, optionButton);
    this.updateVariant();
  }

  selectProduct(swatchButton) {
    this.swatchDropdown.querySelectorAll('.TrendProductCard__Swatch').forEach((button) => {
      button.classList.toggle('is-current', button === swatchButton);
    });
    this.swatchChosen = true;
    this.commitChoice(this.swatchDropdown, swatchButton);
    this.setProduct(Number(swatchButton.dataset.productIndex));
  }

  setProduct(index) {
    const previous = new Map((this.product?.options ?? []).map((option, i) => [option.name, this.selected[i]]));

    this.product = this.data.products[index];
    this.selected = this.product.options.map(({ name, values }) => {
      if (values.length === 1) return values[0];
      return values.includes(previous.get(name)) ? previous.get(name) : null;
    });

    this.soldOut = this.isSoldOut(this.product);
    this.optionsContainer.replaceChildren(
      ...(this.soldOut ? [] : this.product.options.flatMap((option, i) => (option.hidden ? [] : this.createDropdown(option, i))))
    );
    this.renderProduct();
    this.updateVariant();
  }

  createDropdown({ name, values }, index) {
    const dropdown = this.dropdownTemplate.content.firstElementChild.cloneNode(true);
    const toggle = dropdown.querySelector('[data-action="toggle-dropdown"]');
    const list = dropdown.querySelector('.TrendProductCard__DropdownList');
    const listId = `${this.id}-option-${index}`;

    dropdown.dataset.optionIndex = index;
    toggle.setAttribute('aria-controls', listId);
    list.id = listId;
    dropdown.querySelector('[data-dropdown-label]').textContent = this.selected[index] ?? `${this.labels.select} ${name}`;

    list.append(...values.map((value) => {
      const item = document.createElement('li');
      const button = document.createElement('button');

      button.type = 'button';
      button.className = 'TrendProductCard__DropdownOption';
      button.dataset.action = 'select-option';
      button.dataset.value = value;
      button.setAttribute('aria-pressed', String(this.selected[index] === value));
      button.textContent = value;
      item.append(button);
      return item;
    }));

    return dropdown;
  }

  renderProduct() {
    const { image, cardImage, title, name, url, material } = this.product;
    const frontImage = this.querySelector('[data-card-image]');
    const thumb = this.querySelector('[data-card-thumb]');
    const materialLine = this.querySelector('[data-card-material]');

    // Dark tile: custom.alternate_image when set (cardImage), panel thumb + zoom: the product's own media.
    frontImage.parentElement.hidden = !cardImage;
    thumb.parentElement.hidden = !image;

    if (cardImage) {
      Object.assign(frontImage, { src: cardImage.src, srcset: cardImage.srcset, alt: cardImage.alt });
      if (cardImage.width) Object.assign(frontImage, { width: cardImage.width, height: cardImage.height });
    }

    if (image) {
      Object.assign(thumb, { src: image.thumb, width: image.width, height: image.height });
    }

    this.querySelector('[data-card-title]').textContent = name ?? title;
    if (materialLine) {
      materialLine.textContent = material ?? '';
      materialLine.hidden = !material;
    }
    this.panel.setAttribute('aria-label', title);
    this.querySelector('[data-card-link]')?.setAttribute('href', url);
    // Keep the Back-in-Stock trigger pointed at the current swatch's product.
    this.waitlist?.setAttribute('data-product-data', this.product.waitlistData);
  }

  updateVariant() {
    const { variants, hasOnlyDefaultVariant, price } = this.product;
    const optionsComplete = this.selected.every((value) => value !== null);

    this.markUnavailableOptions();

    if (hasOnlyDefaultVariant || this.soldOut) {
      this.variant = variants[0];
    } else {
      this.variant = optionsComplete
        ? variants.find((variant) => variant.options.every((value, i) => value === this.selected[i])) ?? null
        : null;
    }

    this.querySelectorAll('[data-card-price]').forEach((element) => {
      element.textContent = this.soldOut ? price : this.variant?.price ?? price;
    });

    this.setError('');
    this.addButton.disabled = !(this.swatchChosen && this.variant?.available);

    if (!this.swatchChosen || !optionsComplete || this.variant?.available) {
      this.addLabel.textContent = this.labels.add;
    } else {
      this.addLabel.textContent = this.variant ? this.labels.soldOut : this.labels.unavailable;
    }

    // A sold-out variant swaps the add to cart button for the waitlist (BIS) trigger.
    const showWaitlist = Boolean(this.waitlist && this.swatchChosen && this.variant && !this.variant.available);
    this.addButton.hidden = showWaitlist;
    if (this.waitlist) this.waitlist.hidden = !showWaitlist;
  }

  markUnavailableOptions() {
    const { variants } = this.product;

    this.optionDropdowns.forEach((dropdown, index) => {
      dropdown.querySelectorAll('.TrendProductCard__DropdownOption').forEach((button) => {
        const matches = variants.filter((variant) =>
          variant.options[index] === button.dataset.value &&
          variant.options.every((value, i) => i === index || this.selected[i] === null || this.selected[i] === value)
        );
        // No variant with this combination: can't be picked. Sold out: still selectable so the waitlist shows.
        // The current value is never disabled, so a stale combination can always be changed.
        button.disabled = matches.length === 0 && this.selected[index] !== button.dataset.value;
        button.classList.toggle('is-unavailable', matches.length > 0 && !matches.some((variant) => variant.available));
      });
    });
  }

  async addToCart() {
    if (!this.swatchChosen || !this.variant?.available) return;

    this.addButton.disabled = true;
    this.addButton.classList.add('is-loading');
    this.setError('');
    document.dispatchEvent(new CustomEvent('theme:loading:start'));

    try {
      const response = await fetch(`${window.routes.cartAddUrl}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [{ id: this.variant.id, quantity: 1 }] })
      });

      if (!response.ok) {
        const content = await response.json();
        this.setError(content.description ?? content.message);
        return;
      }

      this.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true,
        detail: { variant: this.variant, quantity: 1 }
      }));
      this.closePanel({ restoreFocus: false });
    } catch (error) {
      this.setError(error.message);
    } finally {
      document.dispatchEvent(new CustomEvent('theme:loading:end'));
      this.addButton.classList.remove('is-loading');
      this.addButton.disabled = !(this.swatchChosen && this.variant?.available);
    }
  }

  setError(message) {
    this.error.textContent = message ?? '';
    this.error.hidden = !message;
  }

  openZoom(trigger) {
    const container = this.closest('.TrendProducts')?.querySelector('[data-product-zoom]');
    const { images } = this.product;
    if (!container || !window.PhotoSwipe || images.length === 0) return;

    const maxDimension = window.matchMedia('(max-width: 640px)').matches ? 1200 : 1800;
    const items = images.map(({ src, width, height }) => {
      const scale = Math.min(maxDimension / Math.max(width, height), 1);
      return { src, w: Math.round(width * scale), h: Math.round(height * scale), initialZoomLevel: 0.65 };
    });

    const zoom = new window.PhotoSwipe(container, false, items, {
      index: 0,
      showHideOpacity: true,
      loop: false,
      history: false,
      closeOnVerticalDrag: false,
      pinchToClose: false,
      scaleMode: 'zoom',
      getDoubleTapZoom: (isMouseClick, item) => (isMouseClick ? 1.6 : item.initialZoomLevel < 0.7 ? 1 : 1.33)
    });

    const ui = container.querySelector('.pswp__ui');
    const prev = container.querySelector('.pswp__button--prev');
    const next = container.querySelector('.pswp__button--next');

    const updateNav = () => {
      const index = zoom.getCurrentIndex();
      prev.disabled = index === 0;
      next.disabled = index === items.length - 1;
    };

    const onTap = (event) => {
      const button = event.target.closest('.pswp__button');

      if (button === prev) return zoom.prev();
      if (button === next) return zoom.next();
      if (button?.classList.contains('pswp__button--close')) return zoom.close();

      if (event.target.classList.contains('pswp__img') && event.detail?.pointerType === 'mouse') {
        zoom.toggleDesktopZoom(event.detail.releasePoint);
      }
    };

    container.addEventListener('pswpTap', onTap);
    zoom.listen('beforeChange', updateNav);
    zoom.listen('initialZoomIn', () => ui.classList.remove('pswp__ui--hidden'));
    zoom.listen('initialZoomOut', () => ui.classList.add('pswp__ui--hidden'));
    zoom.listen('destroy', () => {
      container.removeEventListener('pswpTap', onTap);
      trigger.focus();
    });

    zoom.init();
  }
}

if (!customElements.get('trend-product-card')) {
  customElements.define('trend-product-card', TrendProductCard);
}
